import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

import type { IHotelList } from "@/feature/hotel/search/types/hotel-search-page-type";
import type { IHotelsSearchSchema } from "@/feature/hotel/search/types/hotel-search-type";
import type { RootState } from "@/redux/store";

export type CustomUmrahCityKey = "makkah" | "madinah";

export type SerializableHotelSearch = Omit<IHotelsSearchSchema, "date"> & {
  date: {
    from: string;
    to: string;
  };
};

export type SerializableCustomUmrahHotelSelection = {
  search: SerializableHotelSearch;
  hotel: IHotelList;
  captured_payload?: {
    supplier: string;
    search_context: {
      search_id: string;
      checkin: string;
      checkout: string;
      nights: number;
      rooms_requested: Array<{
        adults: number;
        children_ages: number[];
      }>;
    };
    hotel: {
      hotel_code: string;
      name: string;
      category: number;
      address: string;
      city_code: string;
      country: string;
      geolocation: {
        latitude: number;
        longitude: number;
      };
      main_image: string;
    };
    selected_rate: {
      group_code: string;
      rate_key: string;
      room_code: string;
      room_type: string;
      room_references: string[];
      refundable: boolean;
      supports_cancellation: boolean;
      supports_amendment: boolean;
    };
    price: {
      currency: string;
      price: number;
      tax: number;
      total_price: number;
    };
    cancellation_policy: {
      free_cancellation: boolean;
      free_cancellation_last_date: string;
      no_show_fee: number;
      details: Array<{
        from_date: string;
        fee: number;
      }>;
    };
    captured_at: string;
  };
};

export type CustomUmrahFormDraft = {
  routeVehicles: Array<{
    routeId: string;
    vehicleId: string;
  }>;
  includeGuide: boolean;
  email: string;
  phone: string;
  customerNote: string;
};

export type CustomUmrahFlowState = {
  searches: Partial<Record<CustomUmrahCityKey, SerializableHotelSearch>>;
  selections: Partial<
    Record<CustomUmrahCityKey, SerializableCustomUmrahHotelSelection>
  >;
  formDraft: CustomUmrahFormDraft;
};

const initialFormDraft: CustomUmrahFormDraft = {
  routeVehicles: [
    {
      routeId: "",
      vehicleId: "",
    },
  ],
  includeGuide: false,
  email: "",
  phone: "",
  customerNote: "",
};

const initialState: CustomUmrahFlowState = {
  searches: {},
  selections: {},
  formDraft: initialFormDraft,
};

const customUmrahFlowSlice = createSlice({
  name: "customUmrahFlow",
  initialState,
  reducers: {
    hydrateCustomUmrahFlowState: (
      _state,
      action: PayloadAction<CustomUmrahFlowState>,
    ) => action.payload,

    setCustomUmrahHotelSearch: (
      state,
      action: PayloadAction<{
        city: CustomUmrahCityKey;
        search: SerializableHotelSearch;
      }>,
    ) => {
      state.searches[action.payload.city] = action.payload.search;
    },

    setCustomUmrahHotelSelection: (
      state,
      action: PayloadAction<{
        city: CustomUmrahCityKey;
        selection: SerializableCustomUmrahHotelSelection;
      }>,
    ) => {
      state.selections[action.payload.city] = action.payload.selection;
      state.searches[action.payload.city] = action.payload.selection.search;
    },

    setCustomUmrahFormDraft: (
      state,
      action: PayloadAction<CustomUmrahFormDraft>,
    ) => {
      state.formDraft = action.payload;
    },

    clearCustomUmrahHotelSearch: (
      state,
      action: PayloadAction<CustomUmrahCityKey>,
    ) => {
      delete state.searches[action.payload];
    },

    clearCustomUmrahHotelSelection: (
      state,
      action: PayloadAction<CustomUmrahCityKey>,
    ) => {
      delete state.selections[action.payload];
    },

    resetCustomUmrahFlowState: () => initialState,
  },
});

export const {
  clearCustomUmrahHotelSearch,
  clearCustomUmrahHotelSelection,
  hydrateCustomUmrahFlowState,
  resetCustomUmrahFlowState,
  setCustomUmrahFormDraft,
  setCustomUmrahHotelSearch,
  setCustomUmrahHotelSelection,
} = customUmrahFlowSlice.actions;

export const selectCustomUmrahFlow = (state: RootState) => state.customUmrahFlow;

export default customUmrahFlowSlice.reducer;
